import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import StoreIcon from '@mui/icons-material/Store';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import api from '../../services/api';
import { AdminStoreDetail } from '../../types';

const StoreDetailPage: React.FC = () => {
  const { storeId } = useParams<{ storeId: string }>();
  const [store, setStore] = useState<AdminStoreDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStore = async () => {
      if (!storeId) return;
      try {
        const data = await api.adminStore.getStoreDetail(storeId);
        setStore(data);
      } catch (err) {
        console.error('店舗情報の取得に失敗しました', err);
        setError('店舗情報の取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };
    fetchStore();
  }, [storeId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!store) {
    return <Typography color="error">店舗情報が見つかりません</Typography>;
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" color="text.primary" sx={{ mb: 3 }}>
        店舗情報
      </Typography>

      {/* 基本情報 */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          avatar={<StoreIcon color="primary" />}
          title="基本情報"
        />
        <CardContent>
          <InfoRow label="店舗名" value={store.name} />
          <InfoRow label="住所" value={store.address} />
          <InfoRow label="電話番号" value={store.phone} />
          <InfoRow label="LIFF ID" value={store.liff_id} />
          <InfoRow label="登録日" value={store.created_at ? new Date(store.created_at).toLocaleDateString('ja-JP') : undefined} />
          <InfoRow label="最終更新" value={store.updated_at ? new Date(store.updated_at).toLocaleDateString('ja-JP') : undefined} />
        </CardContent>
      </Card>

      {/* LINE連携設定 */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          avatar={<SettingsIcon color="primary" />}
          title="LINE連携設定"
        />
        <CardContent>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 180 }}>
              チャネルシークレット
            </Typography>
            <Chip
              icon={store.has_line_channel_secret ? <CheckCircleIcon /> : <CancelIcon />}
              label={store.has_line_channel_secret ? '設定済み' : '未設定'}
              color={store.has_line_channel_secret ? 'success' : 'default'}
              size="small"
              variant="outlined"
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 180 }}>
              チャネルアクセストークン
            </Typography>
            <Chip
              icon={store.has_line_channel_access_token ? <CheckCircleIcon /> : <CancelIcon />}
              label={store.has_line_channel_access_token ? '設定済み' : '未設定'}
              color={store.has_line_channel_access_token ? 'success' : 'default'}
              size="small"
              variant="outlined"
            />
          </Box>
        </CardContent>
      </Card>

      {/* 支払設定 */}
      {(store.closing_day !== undefined || store.payment_day !== undefined) && (
        <Card>
          <CardHeader
            avatar={<SettingsIcon color="primary" />}
            title="支払設定"
          />
          <CardContent>
            <InfoRow label="締め日" value={store.closing_day != null ? `毎月${store.closing_day}日` : undefined} />
            <Divider sx={{ my: 1 }} />
            <InfoRow label="支払日" value={store.payment_day != null ? `毎月${store.payment_day}日` : undefined} />
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

const InfoRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="body1">
      {value || '-'}
    </Typography>
  </Box>
);

export default StoreDetailPage;
